import type { FC, ReactNode } from "react";
import { usePostHog } from '@posthog/react';
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Alert } from "react-daisyui";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

import useGenerateItinerary from "@/api/itineraries/useGenerateItinerary.ts";
import useTrips from "@/api/trips/useTrips.ts";
import useFinishOnboarding from "@/hooks/useFinishOnboarding.ts";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import ChevronRightIcon from "@/components/ui/icons/ChevronRightIcon.tsx";
import LoaderWrapper from "@/components/ui/LoaderWrapper/LoaderWrapper.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import Routes from "@/router/Routes.ts";
import { avoidLabels } from "@/utils/dna/avoidance.ts";
import { missingTranslationToReadable } from "@/utils/missingTranslationToReadable.ts";
import { bookingLabelKey } from "@/utils/trip/bookingLabel.ts";
import { passToRoute } from "@/utils/passToRoute.ts";
import shouldCollectBookings from "@/utils/shouldCollectBookings.ts";
import TripCreateStep, {
  allTripCreateSteps,
} from "@/_constants/TripCreateStep.ts";
import TravelDnaStep, {
  type TravelDnaStep as TravelDnaStepValue,
} from "@/_constants/TravelDnaStep.ts";
import WhoIsJoining from "$/_const/trip/WhoIsJoining.ts";
import BookingTypes from "$/_const/trip/WhatHasBeenBookedType.ts";
import type { TripData } from "$/models/Trip.model.ts";
import dayjs from "$/utils/dayjs";
import {
  formatBookingMoment,
  formatBookingWhen,
} from "@/utils/formatBookingMoment.ts";

type ConfirmationTripData = TripData & {
  children_ages?: number[];
  number_of_adults?: number;
  special_reason_custom?: string | null;
  apply_saved_accessibility_requirements?: boolean;
  additional_accessibility_requirements?: string | null;
  trip_specific_changes?: string | null;
};

type TripCreateStepValue = (typeof allTripCreateSteps)[number];

type ConfirmationCardProps = {
  title: string;
  children: ReactNode;
  dark?: boolean;
  hidden?: boolean;
  onEdit?: () => void;
};

function ConfirmationCard({
  title,
  children,
  dark = false,
  hidden = false,
  onEdit,
}: ConfirmationCardProps) {
  const { t } = useTranslation();

  return (
    <section
      hidden={hidden}
      className={`rounded-[14px] border border-[#8A6D23] px-5 py-5 sm:px-6 ${
        dark ? "bg-[#3F3F3A] text-white" : "bg-white text-[#191919]"
      }`}
    >
      <div className="flex items-start justify-between gap-6">
        <h2 className="font-feature text-xl font-bold sm:text-2xl">{title}</h2>
        {onEdit ? (
          <button
            type="button"
            className="shrink-0 font-sans text-sm font-semibold text-primary transition-opacity hover:opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
            onClick={onEdit}
          >
            {t("button.edit", "Edit")}
          </button>
        ) : null}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

type ConfirmationRowProps = {
  label: string;
  value: ReactNode;
  isLast?: boolean;
  dark?: boolean;
  onEdit?: () => void;
};

function ConfirmationRow({
  label,
  value,
  isLast = false,
  dark = false,
  onEdit,
}: ConfirmationRowProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`flex flex-col gap-2 py-2 font-sans text-base sm:flex-row sm:items-start sm:justify-between sm:gap-8 ${
        isLast
          ? ""
          : dark
            ? "border-b border-white/25"
            : "border-b border-[#191919]/25"
      }`}
    >
      <span className="shrink-0 text-[#C9A84C]">{label}</span>
      <div className="flex min-w-0 items-start gap-3 sm:max-w-[65%] sm:justify-end">
        <div
          className={`min-w-0 sm:text-right ${
            dark ? "text-white" : "text-[#191919]"
          }`}
        >
          {value}
        </div>
        {onEdit ? (
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            onClick={onEdit}
            aria-label={`${t("button.edit", "Edit")}: ${label}`}
            title={`${t("button.edit", "Edit")}: ${label}`}
          >
            <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

const formatChildAge = (age: number) => {
  if (age < 2) {
    const months = Math.round(age * 12);

    return `${months} ${months === 1 ? "month" : "months"}`;
  }

  return `${age} years`;
};

function RequirementPill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex rounded-full border border-[#8A6D23] bg-[#E8D09A]/35 px-3 py-1 font-sans text-xs font-normal text-[#8A6D23]">
      {children}
    </span>
  );
}

const TripConfirmationPage: FC = () => {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { user, getSubscriptionPage, logout } = useAuthContext();
  const { id: tripId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: trips,
    isLoading: isLoadingTrips,
    error: errorLoadingTrips,
  } = useTrips(!!tripId, {
    id: tripId,
    user_id: user?.id,
  });

  const {
    mutateAsync: generateItinerary,
    isPending: isLoadingGenerateItinerary,
    error: errorGenerateItinerary,
  } = useGenerateItinerary();

  const currentTripData = useMemo(
    () => (trips?.[0]?.data as ConfirmationTripData | undefined) ?? null,
    [trips],
  );

  useFinishOnboarding(currentTripData);

  if (isLoadingTrips) {
    return (
      <div className="fixed inset-0 z-10 min-h-screen w-screen overflow-y-auto bg-journi-theme-light">
        <LoaderWrapper isLoading />
      </div>
    );
  }

  if (!tripId || !currentTripData) {
    return <Navigate to={Routes.DASHBOARD} />;
  }

  const handleCreateTrip = async () => {
    try {
      if (isLoadingGenerateItinerary) return;

      await generateItinerary({ trip_id: tripId });
      posthog?.capture('itinerary_generation_started', {
        trip_id: tripId,
        destination: currentTripData.trip_destination,
        duration_days: calculatedNights,
        who_is_joining: currentTripData.who_is_joining,
      });
      navigate(passToRoute(Routes.TRIP, { id: tripId }));
    } catch (error) {
      console.error(error);

      if (error instanceof Error && error.message === "limit") {
        navigate(getSubscriptionPage());
        return;
      }

      posthog?.captureException(error, {
        operation: 'start_itinerary_generation',
        trip_id: tripId,
      });
      posthog?.capture('itinerary_generation_failed', {
        trip_id: tripId,
        failure_stage: 'queue_request',
      });
    }
  };

  const handleTripEdit = (step: TripCreateStepValue) => {
    const searchParams = new URLSearchParams({
      step,
      returnTo: location.pathname,
    });

    navigate(
      `${passToRoute(Routes.TRIP__EDIT, {
        id: tripId,
      })}?${searchParams.toString()}`,
      {
        state: {
          tripEdit: {
            startStep: step,
            endStep: step,
            returnTo: location.pathname,
          },
        },
      },
    );
  };

  const handleDnaEdit = (step: TravelDnaStepValue) => {
    navigate(Routes.DNA_EDIT, {
      state: {
        dnaEdit: {
          startStep: step,
          endStep: step,
          returnTo: location.pathname,
        },
      },
    });
  };

  const dna = user?.dna?.data;
  const criticalRequirements = user?.dna?.critical_requirements;
  const translateValue = (key: string, value: string) =>
    t(key, { defaultValue: missingTranslationToReadable(value) });
  const notAdded = t("tripOverviewPage.notAdded", "Nothing added");
  const notSelected = t("tripOverviewPage.notSelected", "Not selected");
  const tripPlanningType = currentTripData.trip_planning_type
    ? translateValue(
        `enum_translations.TripPlanningType.${currentTripData.trip_planning_type}`,
        currentTripData.trip_planning_type,
      )
    : notSelected;
  const hasEnteredDates =
    !!currentTripData.arrival_date && !!currentTripData.departure_date;

  const dates = hasEnteredDates
    ? `${dayjs(currentTripData.arrival_date).format("DD/MM/YY")} – ${dayjs(
        currentTripData.departure_date,
      ).format("DD/MM/YY")}`
    : currentTripData.duration
      ? t("tripOverviewPage.approximateDuration", {
          count: currentTripData.duration,
          defaultValue: `Approximately ${currentTripData.duration} days`,
        })
      : t("tripOverviewPage.datesUndecided", "Dates not decided");

  const calculatedNights = hasEnteredDates
    ? Math.max(
        1,
        dayjs(currentTripData.departure_date).diff(
          dayjs(currentTripData.arrival_date),
          "day",
        ),
      )
    : currentTripData.duration;

  const flexibility = currentTripData.can_modify_arrival_or_departure_dates
    ? translateValue(
        `enum_translations.DateFlexibility.${currentTripData.can_modify_arrival_or_departure_dates}`,
        currentTripData.can_modify_arrival_or_departure_dates,
      )
    : notSelected;

  const whoIsJoining = currentTripData.who_is_joining
    ? translateValue(
        `enum_translations.WhoIsJoining.${currentTripData.who_is_joining}`,
        currentTripData.who_is_joining,
      )
    : notSelected;

  const isFamily = currentTripData.who_is_joining === WhoIsJoining.FAMILY;
  const childrenAges = currentTripData.children_ages ?? [];
  const occasion = currentTripData.special_reason_custom?.trim()
    ? currentTripData.special_reason_custom
    : currentTripData.is_it_special_reason_for_trip
      ? translateValue(
          `enum_translations.SpecialReasonForTrip.${currentTripData.is_it_special_reason_for_trip}`,
          currentTripData.is_it_special_reason_for_trip,
        )
      : notSelected;

  const pace = dna?.how_do_you_like_to_travel
    ? translateValue(
        `travelDnaPage.howDoYouLikeToTravel.${dna.how_do_you_like_to_travel}`,
        dna.how_do_you_like_to_travel,
      )
    : notSelected;

  const accommodationStyle = dna?.what_type_of_place_do_you_prefer
    ? translateValue(
        `travelDnaPage.whatTypeOfPlaceDoYouPrefer.${dna.what_type_of_place_do_you_prefer}`,
        dna.what_type_of_place_do_you_prefer,
      )
    : notSelected;

  const homeBase =
    dna?.where_do_you_feel_most_at_home
      ?.map((item) =>
        translateValue(`travelDnaPage.whereDoYouFeelMostAtHome.${item}`, item),
      )
      .join(", ") || notSelected;

  const diningStyle = dna?.your_approach_to_food_when_you_travel
    ? translateValue(
        `travelDnaPage.yourApproachToFoodWhenYouTravel.${dna.your_approach_to_food_when_you_travel}`,
        dna.your_approach_to_food_when_you_travel,
      )
    : notSelected;

  const foodPreferences =
    criticalRequirements?.food_preferences
      ?.map((item) =>
        translateValue(`travelDnaPage.yourFoodPreferences.options.${item}`, item),
      )
      .join(", ") || notSelected;

  const avoids = avoidLabels(dna, translateValue).join(", ");

  const savedAccessibilityRequirements =
    criticalRequirements?.accessibility_requirements ?? [];
  const savedAccessibilityCustom =
    criticalRequirements?.accessibility_requirements_custom?.trim();
  const accessibilityLabels = [
    ...savedAccessibilityRequirements.map((item) =>
      t(
        [
          `travelDnaPage.accessibilityRequirements.${item}`,
          `travelDnaPage.accessAndSupport.${item}`,
        ],
        { defaultValue: missingTranslationToReadable(item) },
      ),
    ),
    ...(savedAccessibilityCustom ? [savedAccessibilityCustom] : []),
  ];

  const showBookings = shouldCollectBookings(currentTripData);
  const bookings = showBookings
    ? (currentTripData.what_has_been_booked ?? [])
    : [];
  const bookingRows = bookings.map((booking, index) => {
    const provider = booking.provider?.trim();
    const location = booking.location?.trim();
    const departureProvider = booking.departureProvider?.trim();
    const departureLocation = booking.departureLocation?.trim();
    const reference = booking.bookingRef?.trim();
    const headline = provider || location || notAdded;
    const arrivalAirport = location || headline;
    const departureAirport = departureLocation || headline;
    const details = (booking.bookingType === BookingTypes.FLIGHT
      ? [
        booking.dateTime?.trim()
          ? `${t(
            "tripCreateForm.whatHasBeenBooked.bookingSummary.arrivalFlightLabel",
            "Arriving at",
          )} ${arrivalAirport}: ${formatBookingMoment(booking.dateTime.trim())}`
          : null,
        booking.end?.trim()
          ? `${t(
            "tripCreateForm.whatHasBeenBooked.bookingSummary.departureFlightLabel",
            "Departing from",
          )} ${departureAirport}: ${formatBookingMoment(booking.end.trim())}`
          : null,
        departureProvider ? departureProvider : null,
        reference
          ? t("tripCreateForm.whatHasBeenBooked.badgeReference", {
            reference,
            defaultValue: `Ref ${reference}`,
          })
          : null,
        booking.notes?.trim(),
      ]
      : [
        formatBookingWhen(booking.dateTime, booking.end),
        location === headline ? null : location,
        reference
          ? t("tripCreateForm.whatHasBeenBooked.badgeReference", {
            reference,
            defaultValue: `Ref ${reference}`,
          })
          : null,
        booking.notes?.trim(),
      ]).filter((detail): detail is string => !!detail);

    return {
      key: booking.id ?? `booking-${index + 1}`,
      label: t(
        bookingLabelKey(booking),
        missingTranslationToReadable(booking.bookingType),
      ),
      headline,
      details,
      hasDate: !!booking.dateTime?.trim(),
    };
  });

  const errorMessages = [
    errorLoadingTrips?.message,
    errorGenerateItinerary?.message,
  ].filter((message): message is string => !!message);

  return (
    <div className="fixed inset-0 z-10 min-h-screen w-screen overflow-x-hidden overflow-y-auto bg-journi-theme-light text-[#191919]">
      <header className="w-full bg-journi-black px-5 py-5 text-white sm:px-8 md:px-14 lg:px-20">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="font-sans text-sm font-semibold uppercase tracking-wide text-primary">
              {t("tripOverviewPage.eyebrow", "Your trip")}
            </p>
            <h1 className="mt-4 max-w-[1440px] font-feature text-4xl font-bold leading-tight md:text-5xl">
              {t("tripOverviewPage.yourTrip", "Your trip")}
              {currentTripData.trip_destination ? (
                <>
                  {" "}
                  {t("tripOverviewPage.to", "to")}{" "}
                  <span className="italic text-primary">
                    {currentTripData.trip_destination}
                  </span>
                </>
              ) : null}
              {calculatedNights ? (
                <>
                  {" "}
                  {t("tripOverviewPage.for", "for")}{" "}
                  <span className="italic text-primary">
                    {t("tripOverviewPage.nights", {
                      count: calculatedNights,
                      defaultValue: `${calculatedNights} nights`,
                    })}
                  </span>
                </>
              ) : null}
            </h1>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-[14px] bg-white px-5 py-3 font-sans text-base font-medium text-error hover:bg-white/90"
            onClick={logout}
          >
            {t("button.logout", "Logout")}
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1440px] px-5 py-8 sm:px-8 md:py-10">
        {errorMessages.length > 0 ? (
          <div className="mb-6 flex flex-col gap-3">
            {errorMessages.map((message) => (
              <Alert key={message} status="error">
                {message}
              </Alert>
            ))}
          </div>
        ) : null}

        <p className="text-center font-sans text-base text-text-secondary">
          {t(
            "tripOverviewPage.introduction",
            "Review each section below, or jump back to any question to make changes.",
          )}
        </p>

        <div className="mt-7 flex flex-col gap-6">
          <ConfirmationCard
            title={t(
              "tripOverviewPage.sections.dna",
              "Your existing Travel DNA",
            )}
            dark
          >
            <ConfirmationRow
              label={t("tripOverviewPage.labels.pace", "Pace")}
              value={pace}
              onEdit={() => handleDnaEdit(TravelDnaStep.YOUR_PACE)}
              dark
            />
            <ConfirmationRow
              label={t(
                "tripOverviewPage.labels.accommodationStyle",
                "Where you stay",
              )}
              value={accommodationStyle}
              onEdit={() => handleDnaEdit(TravelDnaStep.YOUR_STAY)}
              dark
            />
            <ConfirmationRow
              label={t(
                "tripOverviewPage.labels.homeBase",
                "Feels most like home",
              )}
              value={homeBase}
              onEdit={() => handleDnaEdit(TravelDnaStep.YOUR_SANCTUARY)}
              dark
            />
            <ConfirmationRow
              label={t(
                "tripOverviewPage.labels.diningStyle",
                "When eating out",
              )}
              value={diningStyle}
              onEdit={() => handleDnaEdit(TravelDnaStep.YOUR_TABLE)}
              dark
            />
            <ConfirmationRow
              label={t(
                "tripOverviewPage.labels.foodPreferences",
                "Food preferences",
              )}
              value={foodPreferences}
              onEdit={() =>
                handleDnaEdit(TravelDnaStep.YOUR_FOOD_PREFERENCES)
              }
              dark
            />
            <ConfirmationRow
              label={t("tripOverviewPage.labels.avoid", "Avoid")}
              value={avoids || notSelected}
              onEdit={() => handleDnaEdit(TravelDnaStep.YOUR_DEFAULTS)}
              isLast
              dark
            />
          </ConfirmationCard>

          <ConfirmationCard
            title={t("tripOverviewPage.sections.trip", "Your trip")}
            onEdit={() => handleTripEdit(TripCreateStep.DESTINATION)}
          >
            <ConfirmationRow
              label={t("tripOverviewPage.labels.tripType", "Trip type")}
              value={tripPlanningType}
            />
            <ConfirmationRow
              label={t("tripOverviewPage.labels.destination", "Destination")}
              value={currentTripData.trip_destination || notSelected}
              isLast
            />
          </ConfirmationCard>

          <ConfirmationCard
            title={t("tripOverviewPage.sections.dates", "Dates & flexibility")}
            onEdit={() => handleTripEdit(TripCreateStep.DATE)}
          >
            <ConfirmationRow
              label={t("tripOverviewPage.labels.dates", "Dates")}
              value={dates}
              isLast={!hasEnteredDates}
            />
            {hasEnteredDates ? (
              <ConfirmationRow
                label={t("tripOverviewPage.labels.flexibility", "Flexibility")}
                value={flexibility}
                isLast
              />
            ) : null}
          </ConfirmationCard>

          <ConfirmationCard
            title={t(
              "tripOverviewPage.sections.travellers",
              "Who’s going & the occasion",
            )}
            onEdit={() => handleTripEdit(TripCreateStep.WITH_WHO)}
          >
            <ConfirmationRow
              label={t("tripOverviewPage.labels.whoIsJoining", "Who’s joining")}
              value={whoIsJoining}
            />
            {isFamily ? (
              <ConfirmationRow
                label={t(
                  "tripOverviewPage.labels.numberOfChildren",
                  "How many children",
                )}
                value={childrenAges.length}
              />
            ) : null}
            {isFamily && childrenAges.length > 0 ? (
              <ConfirmationRow
                label={t(
                  "tripOverviewPage.labels.childrenAges",
                  "Ages of children joining",
                )}
                value={childrenAges.map(formatChildAge).join(", ")}
              />
            ) : null}
            <ConfirmationRow
              label={t("tripOverviewPage.labels.occasion", "What’s the occasion")}
              value={occasion}
              isLast
            />
          </ConfirmationCard>

          <ConfirmationCard
            title={t(
              "tripOverviewPage.sections.accessibility",
              "Access & support",
            )}
            onEdit={() => handleTripEdit(TripCreateStep.WITH_WHO)}
          >
            <ConfirmationRow
              label={t(
                "tripOverviewPage.labels.savedAccessibility",
                "Accessibility needs",
              )}
              value={
                accessibilityLabels.length > 0 ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    {accessibilityLabels.map((label) => (
                      <RequirementPill key={label}>{label}</RequirementPill>
                    ))}
                    <span className="w-full font-sans text-xs text-text-secondary">
                      {t("tripOverviewPage.applicableForTrip", {
                        defaultValue: "Applicable for this trip: {{answer}}",
                        answer:
                          currentTripData.apply_saved_accessibility_requirements
                            ? t("button.yes", "Yes")
                            : t("button.no", "No"),
                      })}
                    </span>
                  </div>
                ) : (
                  notAdded
                )
              }
            />
            <ConfirmationRow
              label={t(
                "tripOverviewPage.labels.tripAccessibility",
                "Trip-specific accessibility needs",
              )}
              value={
                currentTripData.additional_accessibility_requirements?.trim() ||
                notAdded
              }
              isLast
            />
          </ConfirmationCard>

          <ConfirmationCard
            hidden={!showBookings}
            title={t(
              "tripOverviewPage.sections.bookings",
              "What’s already booked",
            )}
            onEdit={() =>
              handleTripEdit(TripCreateStep.WHAT_HAS_BEEN_BOOKED)
            }
          >
            {bookingRows.length > 0 ? (
              bookingRows.map((row, index) => (
                <ConfirmationRow
                  key={row.key}
                  label={row.label}
                  value={
                    <div className="flex flex-col gap-1 sm:items-end">
                      <span className="font-semibold">{row.headline}</span>
                      {row.details.map((detail, detailIndex) => (
                        <span
                          key={`${row.key}-${detailIndex}`}
                          className="font-sans text-sm text-text-secondary"
                        >
                          {detail}
                        </span>
                      ))}
                      {row.hasDate ? null : (
                        <span className="font-sans text-sm text-error">
                          {t(
                            "tripOverviewPage.bookings.missingDate",
                            "Add a date and time so we can plan around it",
                          )}
                        </span>
                      )}
                    </div>
                  }
                  isLast={index === bookingRows.length - 1}
                />
              ))
            ) : (
              <ConfirmationRow
                label={t("tripOverviewPage.labels.bookings", "Bookings")}
                value={notAdded}
                isLast
              />
            )}
          </ConfirmationCard>

          <ConfirmationCard
            title={t(
              "tripOverviewPage.sections.changes",
              "Anything different this time",
            )}
            onEdit={() => handleTripEdit(TripCreateStep.MORE_INFO)}
          >
            <ConfirmationRow
              label={t(
                "tripOverviewPage.labels.anythingDifferent",
                "Anything different",
              )}
              value={currentTripData.trip_specific_changes?.trim() || notAdded}
            />
            <ConfirmationRow
              label={t(
                "tripOverviewPage.labels.mustInclude",
                "One thing this trip must include",
              )}
              value={currentTripData.additional_requests?.trim() || notAdded}
              isLast
            />
          </ConfirmationCard>
        </div>

        <div className="mt-8 flex justify-center pb-8">
          <LoadingButton
            loading={isLoadingGenerateItinerary}
            type="button"
            color="primary"
            className="journi-primary-button w-full sm:w-[250px]"
            endIcon={<ChevronRightIcon className="h-3.5 w-3.5" />}
            onClick={() => void handleCreateTrip()}
          >
            {t("tripOverviewPage.createTrip", "Create trip")}
          </LoadingButton>
        </div>
      </main>
    </div>
  );
};

export default TripConfirmationPage;
